/**
 * UserCompanyAccessTab — F12.M Etap D Part 2 (Users fullscreen, Etap 1.4).
 *
 * Zakładka „Dostępy do firm" w detail user'a. Wyciągnięte z `DrawerCompaniesSection`
 * w `Users.tsx` jako standalone component.
 *
 * Funkcjonalność:
 *  - Lista firm do których user ma dostęp + rola per firma (inline edit dropdown).
 *  - Expand/collapse szczegóły permission overrides per firma.
 *  - „Dodaj dostęp do firmy" (picker firmy + rola).
 *  - Revoke dostępu do firmy (delete /api/companies/:companyId/members/:userId).
 *  - Super-user: info „bypass" zamiast listy (super-user widzi wszystkie firmy).
 *
 * Per decyzji B (Eugenia 2026-05-13): pełna matryca uprawnień zostaje w
 * Settings → Uprawnienia użytkowników. Tu link „Edytuj pełne uprawnienia →".
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Settings } from 'lucide-react';

import { InfoHint } from '@/components/InfoHint';
import { AddCompanyForm, CompanyAccessRow } from '@/components/settings/UsersPermissionsTab';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';
import {
  useCompanies,
  USER_PERMISSIONS_KEY,
  type UserCompanyAccess,
  useRoleTemplates,
  USERS_LIST_KEY,
  useUserPermissions,
} from '@/lib/use-permissions';
import { USER_DETAIL_KEY } from '@/lib/use-tenant-people';

interface Props {
  userId: string;
  isSuperUser: boolean;
}

export function UserCompanyAccessTab({ userId, isSuperUser }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const userPermsQ = useUserPermissions(userId);
  const rolesQ = useRoleTemplates();
  const companiesQ = useCompanies();

  const [showAddCompany, setShowAddCompany] = useState(false);

  const assignMutation = useMutation({
    mutationFn: async (input: {
      companyId: string;
      /** Komplet ról w kolejności (ADR-0103). Gdy podany — wygrywa z `roleTemplateId`. */
      roleTemplateIds?: string[];
      roleTemplateId?: string;
    }) => {
      const body: Record<string, unknown> = { userId };
      if (input.roleTemplateIds) body.roleTemplateIds = input.roleTemplateIds;
      else body.roleTemplateId = input.roleTemplateId;
      const res = await api.post(`/companies/${input.companyId}/members`, body);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_PERMISSIONS_KEY(userId) });
      queryClient.invalidateQueries({ queryKey: USER_DETAIL_KEY(userId) });
      queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
      toast.show({ variant: 'success', title: t('tenant.users.toast.roleSaved') });
    },
    onError: (err) =>
      toast.show({
        variant: 'error',
        title: t('tenant.users.toast.roleSaveError'),
        description: extractErrorCode(err),
      }),
  });

  // Wsadowe przypisanie do wielu firm jedną rolą (multi-select) — jedno żądanie.
  const batchAssignMutation = useMutation({
    mutationFn: async (input: { companyIds: string[]; roleKeys: string[] }) => {
      const res = await api.post('/companies/members/batch', { userId, ...input });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_PERMISSIONS_KEY(userId) });
      queryClient.invalidateQueries({ queryKey: USER_DETAIL_KEY(userId) });
      queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
      toast.show({ variant: 'success', title: t('tenant.users.toast.roleSaved') });
    },
    onError: (err) =>
      toast.show({
        variant: 'error',
        title: t('tenant.users.toast.roleSaveError'),
        description: extractErrorCode(err),
      }),
  });

  const revokeMutation = useMutation({
    mutationFn: async (companyId: string) => {
      const res = await api.delete(`/companies/${companyId}/members/${userId}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_PERMISSIONS_KEY(userId) });
      queryClient.invalidateQueries({ queryKey: USER_DETAIL_KEY(userId) });
      queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
      toast.show({ variant: 'success', title: t('tenant.users.toast.companyRevoked') });
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      if (code === 'LAST_COMPANY_ACCESS') {
        toast.show({
          variant: 'warning',
          title: t('tenant.users.toast.lastCompanyTitle'),
          description: t('tenant.users.toast.lastCompanyDescription'),
          duration: 7000,
        });
        return;
      }
      toast.show({
        variant: 'error',
        title: t('tenant.users.toast.companyRevokeError'),
        description: code,
      });
    },
  });

  if (isSuperUser) {
    return (
      <div className="p-4" data-testid="user-company-access-tab-super">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
          {t('tenant.users.detail.superUserBypass')}
        </div>
      </div>
    );
  }

  if (userPermsQ.isLoading || rolesQ.isLoading || companiesQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-10" role="status">
        <Spinner />
      </div>
    );
  }

  const accesses = userPermsQ.data?.companies ?? [];
  const allCompanies = companiesQ.data?.companies ?? [];
  const roles = rolesQ.data?.roleTemplates ?? [];

  const availableCompanies = allCompanies.filter(
    (c) => !accesses.some((a) => a.companyId === c.id),
  );

  const busy = assignMutation.isPending || revokeMutation.isPending;

  return (
    <div className="space-y-3 p-4" data-testid="user-company-access-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-tertiary">{t('tenant.users.detail.companyAccessHint')}</p>
        <Link
          to="/settings/permissions"
          className="inline-flex items-center gap-1 text-xs text-accent-700 hover:underline"
          data-testid="user-company-access-settings-link"
        >
          <Settings size={12} aria-hidden="true" />
          {t('tenant.users.detail.fullPermissionsLink')}
        </Link>
      </div>

      {accesses.length > 0 && <InfoHint>{t('tenant.users.roles.hint')}</InfoHint>}

      <div className="space-y-2">
        {accesses.length === 0 && !showAddCompany && (
          <p className="rounded-lg border border-dashed border-border-secondary p-6 text-center text-sm text-text-tertiary">
            {t('tenant.users.detail.noCompanies')}
          </p>
        )}

        {accesses.map((access: UserCompanyAccess) => (
          <CompanyAccessRow
            key={access.companyId}
            access={access}
            roles={roles}
            onRolesChange={(roleIds) =>
              assignMutation.mutate({ companyId: access.companyId, roleTemplateIds: roleIds })
            }
            onRevoke={() => revokeMutation.mutate(access.companyId)}
            busy={busy}
          />
        ))}

        {showAddCompany ? (
          <AddCompanyForm
            available={availableCompanies}
            roles={roles}
            onCancel={() => setShowAddCompany(false)}
            onSubmit={(companyIds, roleKeys) => {
              batchAssignMutation.mutate({ companyIds, roleKeys });
              setShowAddCompany(false);
            }}
          />
        ) : availableCompanies.length > 0 ? (
          <button
            type="button"
            onClick={() => setShowAddCompany(true)}
            className="flex items-center gap-2 rounded-lg border border-dashed border-border-secondary px-3 py-2 text-sm text-accent-700 hover:bg-accent-50 dark:hover:bg-accent-900/20"
            data-testid="user-company-access-add-btn"
          >
            <Plus size={14} aria-hidden="true" />
            {t('tenant.users.detail.addCompany')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
